import { GridPinnedColumnFields } from "@mui/x-data-grid-premium";
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  AreaItemType,
  DimensionField,
  FieldConfiguration,
  GroupingField,
  Ordering,
  ReportField,
  SortField,
} from "../../../shared/reporting/api/biClient.types";
import { handlePinnedColumnsChange } from "../../../shared/reporting/components/grid/tabular/utils/pinnedColumnsHelper.ts";
import { isAggregatedType, isDimensionBased } from "../../../shared/reporting/utils/fieldsHelper.ts";
import { getGroupMetaNames } from "../../components/builder/common/utilities/fieldsState.ts";
import {
  selectTabularFreezedColumns,
  selectTabularGrouping,
  selectTabularShowGroupedColumns,
} from "../selectors/fieldsStateSelectors.ts";
import { createTypedAsyncThunk } from "../utils/createTypedAsyncThunk.ts";
import { createTabularAction } from "../utils/storeUtils.ts";

const updateTabularFieldConfig = createTypedAsyncThunk<
  void,
  { field: ReportField; changes: Partial<FieldConfiguration> }
>(
  "tabularFields/updateFieldConfig",
  (payload: { field: ReportField; changes: Partial<FieldConfiguration> }, { dispatch, getState }) => {
    const { field, changes } = payload;
    dispatch(createTabularAction("updateFieldConfig", payload));
    if (!isDimensionBased(field)) {
      return;
    }
    const isAggregatedSwitchedOn = isAggregatedType(changes);
    const state = getState();
    const groupingField = state.fieldsState.tabularFieldsReducer.grouping.find((g) => g.id === field.config.guid);
    if (groupingField && isAggregatedSwitchedOn) {
      dispatch(removeGroup(groupingField));
    }

    const sortingField = state.fieldsState.tabularFieldsReducer.sorts.find((s) => s.config.id === field.config.guid);
    if (!sortingField) {
      return;
    }
    if (field.config?.customLabel !== changes.customLabel) {
      dispatch(
        createTabularAction("updateSortingConfig", {
          field: sortingField,
          changes: { caption: changes.customLabel },
        })
      );
    }
    if (isAggregatedSwitchedOn) {
      dispatch(createTabularAction("removeSorting", sortingField));
    }
  }
);

const setGrouping = createAsyncThunk<void, GroupingField[]>(
  "tabularFields/setGrouping",
  (groups: GroupingField[], { dispatch }) => {
    dispatch(createTabularAction("setGrouping", groups));
  }
);

const updateGroup = createAsyncThunk<void, { group: GroupingField; changes: Partial<GroupingField> }>(
  "tabularFields/updateGroup",
  (payload: { group: GroupingField; changes: Partial<GroupingField> }, { dispatch }) => {
    dispatch(createTabularAction("updateGroup", payload));
  }
);

const addGroup = createTypedAsyncThunk<void, GroupingField>(
  "tabularFields/addGroup",
  (group: GroupingField, { dispatch, getState }) => {
    const state = getState();

    const field = state.fieldsState.tabularFieldsReducer.fields.find((f) => f.config.guid === group.id);
    if (field && field.meta && isDimensionBased(field)) {
      const optimisticGroups = [...state.fieldsState.tabularFieldsReducer.grouping, group];
      const sortingField = state.fieldsState.tabularFieldsReducer.sorts.find((s) => s.config.id === field.config.guid);
      if (sortingField) {
        dispatch(createTabularAction("removeSorting", sortingField));
      }
      dispatch(
        addGroupSorting({
          field,
          groupMetaNames: getGroupMetaNames(optimisticGroups, state.fieldsState.tabularFieldsReducer.fields),
        })
      );
    }
    dispatch(createTabularAction("addGroup", group));
  }
);

const removeGroup = createTypedAsyncThunk<void, GroupingField>(
  "tabularFields/removeGroup",
  (group: GroupingField, { dispatch, getState }) => {
    const state = getState();
    const field = state.fieldsState.tabularFieldsReducer.fields.find((f) => f.config.guid === group.id);
    if (field && field.meta && isDimensionBased(field)) {
      const sortingField = state.fieldsState.tabularFieldsReducer.sorts.find((s) => s.config.id === field.config.guid);
      if (sortingField) {
        dispatch(createTabularAction("removeSorting", sortingField));
      }
    }

    dispatch(createTabularAction("removeGroup", group));
  }
);

const moveGroup = createAsyncThunk<void, { field: GroupingField; newIndex: number }>(
  "tabularFields/moveGroup",
  (payload: { field: GroupingField; newIndex: number }, { dispatch }) => {
    dispatch(createTabularAction("moveGroup", payload));
  }
);

const addGroupSorting = createAsyncThunk<void, { field: DimensionField; groupMetaNames: string[] }>(
  "tabularFields/addGroupSorting",
  (payload: { field: DimensionField; groupMetaNames: string[] }, { dispatch }) => {
    const { field, groupMetaNames } = payload;
    const conditionField: SortField = {
      meta: field.meta,
      config: {
        name: field.meta.name,
        id: field.config.guid,
        ordering: Ordering.Ascending,
        caption: field.config.customLabel || field.meta.caption,
        isGroupField: true,
      },
      areaItemType: AreaItemType.SORTS,
    };
    dispatch(createTabularAction("addGroupSorting", { field: conditionField, groupMetaNames }));
  }
);

const setFreezedColumns = createTypedAsyncThunk<void, GridPinnedColumnFields>(
  "tabularFields/setFreezedColumns",
  (freezedColumns: GridPinnedColumnFields, { dispatch, getState }) => {
    const state = getState();
    const freezedColumnIds = selectTabularFreezedColumns(state);
    const groupAreaValues = selectTabularGrouping(state);
    const showGroupedColumns = selectTabularShowGroupedColumns(state);

    const newFreezedColumns = handlePinnedColumnsChange(
      freezedColumns,
      freezedColumnIds,
      groupAreaValues,
      showGroupedColumns
    );
    dispatch(createTabularAction("updateSettings", { freezedColumns: newFreezedColumns }));
  }
);

export const tabularFieldsStateThunks = {
  updateTabularFieldConfig,
  setGrouping,
  updateGroup,
  addGroup,
  removeGroup,
  moveGroup,
  addGroupSorting,
  setFreezedColumns,
};
